/**
 * @name UDS_CONFIG_REQUEST
 * @description Initializes the Unified Deal Starter with configuration options  and inventory data. This data is passed into the deal starter components to control user experience and functionality.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:calcConfig:request', {
 *    externalConfig,
 *    listing,
 * });
 * @example <caption>Event Payload</caption>
 * {
 *  externalConfig: {
 *     supportedDealTypes: [
 *         "finance",
 *         "lease",
 *         "cash"
 *     ],
 *     initialDealType: "finance",
 *     msrp: 0,
 *     cashDealPrice: 30000,
 *     listingPrice: 200000,
 *     enableNegotiation: false,
 *     showMsrp: true,
 *     enablePriceBreakdown: false,
 *     enablePriceDetailsInOfferBreakdown: true,
 *     enableOfferUpgrades: true,
 *     includeNewVehicleIncentives: true,
 *     enableTaxesAndFees: true,
 *     includeTaxesAndFees: true,
 *     selectedCriteria: {
 *         selectedLoanTerm: 60,
 *         selectedCreditType: "verygood",
 *         leaseDownPayment: null,
 *         financeDownPayment: 2000,
 *         financeDownPaymentPercentage: null,
 *         interestRate: {
 *             new: 3.54,
 *             used: 2.23
 *         },
 *         selectedLeaseTerm: 24,
 *         selectedLeaseMileage: 12000,
 *         leaseMileageOptions: [
 *             7500,
 *             10000,
 *             12000,
 *             15000
 *         ],
 *         leaseTermOptions: [
 *             24,
 *             36,
 *             39,
 *             48
 *         ],
 *         financeTermOptions: [
 *             24,
 *             36,
 *             48,
 *             60,
 *             72,
 *         ];
 *     },
 *     ecommerce: "CoxAuto",
 *     partnerId: "MMD", // AMK or MMD
 *     dealerId: 2368, // mmdId
 *     vin: "3KPFK4A74JE218684",
 *     chromeStyleId: 397041,
 *     shopperZip: '30350',
 *     dealerZip: '94538',
 *     mileage: 73316,
 *     esntialEligibilityUrl: "https://awsdripp.drimotors.com/api/search/verify",
 *     esntialDeepLink: {
 *         href: "https://www.roimotors.com/shopping-cart?vin=3KPFK4A74JE218684",
 *     },
 *     showLenderName: false,
 *     showNationalAvg:false,
 *     showLendingOptions: false,
 *     showWalletSyncBanner: false,
 *     disclaimerKey: "CoxAuto",
 *     isSingleColumn: false,
 *     enableDealReceipt: false,
 *     disableTrade: false,
 *     enablePayoffQuote: true,
 *     hideSendOfferCTA: true,
 *     digitalRetailingType: "default", // default, accelerate
 *  }
 *  listing: {
 *     id: 462818379,
 *     owner: owner.id,
 *     pricingDetail: {
 *         msrp: 200500,
 *         salePrice: 200500,
 *     },
 *     chromeStyleId: 397041,
 *     mileage: 73316,
 *     make: 'Volvo',
 *     ownerName: 'Dealer Name',
 *     type: 'New', // New, Used, or Certified
 *     vin: "3KPFK4A74JE218684",
 *     images: ['https://pictures.dealer.com/r/roimotorsdemo/1764/0fc0cd4f30593ecafc921ed3754ba22ex.jpg'], // Accelerate tenant
 *stockNumber: '74339',
 *  }
 * }
 */
export const UDS_CONFIG_REQUEST = 'uds:calcConfig:request';

/**
 * @name UDS_CONFIG_COMPLETED
 * @description This event is fired internally after the data sent in with `UDS_CONFIG_REQUEST` is transformed. That transformation includes applying tenant-level configuration settings based on the `ecommerce` value in the initial payload, as well as any business logic such as applying some settings only to certain vehicle conditions, etc.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.on('uds:calcConfig:completed', {
 *    externalConfig,
 *     listing,
 * });
 * @example <caption>Event Payload</caption>
 * {
 *     externalConfig,
 *     listing,
 * }
 */
export const UDS_CONFIG_COMPLETED = 'uds:calcConfig:completed';

/**
 * @name UDS_CONFIG_UPDATE_REQUEST
 * @description This event sends a request for new payment info after a user updates input values used for calculation, also known as penciling.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:calcConfigUpdate:request', {
 *     payments,
 *     config: externalConfig,
 * });
 * @example <caption>Event Payload</caption>
 * {
 *   payments: {
 *     creditAppDeals: [],
 *     creditType: 'b',
 *     leaseMileageRange: 12000,
 *     leaseTerm: 36,
 *     loanTerm: 60,
 *     downPayment: null,
 *     downPaymentPercent: 0.2,
 *     budget: null,
 *     dealType: null,
 *     interestRate: {
 *         new: 6.0,
 *         used: 7.05,
 *     },
 *     tradeIn: {
 *         value: null,
 *         amountOwed: null,
 *         condition: null,
 *         source: null,
 *         ico: [],
 *     },
 *     monthlyBudget: null,
 *     lastUpdated: null,
 *     createdDate: null,
 *     interactions: {
 *             hasUpdatedNDSCalc: false,
 *             hasUpdatedWallet: false,
 *             hasRequestedTaxesFees: false,
 *             paymentEmphasis: false,
 *     },
 *     paymentOption: 'finance',
 *     creditVerification: {
 *         verified: false,
 *         verifiedDate: null,
 *     },
 *     zip: null,
 *     timeStamp: null,
 *     walletId: null,
 *   },
 *  config: externalConfig,
 * }
 */
export const UDS_CONFIG_UPDATE_REQUEST = 'uds:calcConfigUpdate:request';

/**
 * @name UDS_CONFIG_UPDATE_COMPLETED
 * @description This event is fired internally after the data sent in with `UDS_CONFIG_UPDATE_REQUEST` is transformed and applied to the config and returns the modified config back to UDS.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.on('uds:calcConfigUpdate:completed', {
 *     externalConfig,
 * });
 * @example <caption>Event Payload</caption>
 * {
 *    externalConfig,
 * }
 */
export const UDS_CONFIG_UPDATE_COMPLETED = 'uds:calcConfigUpdate:completed';

/**
 * @name UDS_PAYMENT_UPDATE_REQUEST
 * @description This event sends a request for new payment info after a user updates input values which is used for calculation, also known as penciling.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:paymentUpdate:request', payload)
 * @example <caption>Event Payload</caption>
 * {
 *     dealType: 'finance',
 *     downPayment: 2000,
 *     creditTier: "b",
 *     leaseTerm: 24,
 *     loanTerm: 60,
 *     tradeInValue: 2000,
 *     amountOwed: 500,
 *     userZip: 30456,
 *     mileage: 7500,
 * }
 */
export const UDS_PAYMENT_UPDATE_REQUEST = 'uds:paymentUpdate:request';

/**
 * @name UDS_PAYMENT_UPDATE_COMPLETED
 * @description This event sent by the external tenant when updated payment info is provided as a result of new pencilling data sent as part of request 'UDS_PAYMENT_UPDATE_REQUEST'
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:paymentUpdate:completed', payload)
 * @example <caption>Event Payload</caption>
 * {
 *     rates: {
 *             a: {
 *                 paymentOffers: {
 *                     36: [
 *                         {
 *                             offeredBy: 'Mercedes-Benz Financial Services USA LLC.',
 *                             payment: 3333.35,
 *                             new: 7.94,
 *                             downPayment: 4444,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                             totalOfPayments: 16392.6,
 *                             specialApr: true,
 *                             totalInterest: 3245,
 *                         },
 *                     ],
 *                     60: [
 *                         {
 *                             offeredBy: 'Mercedes-Benz Financial Services USA LLC.',
 *                             payment: 3333.35,
 *                             new: 7.94,
 *                             downPayment: 4444,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                             totalOfPayments: 16392.6,
 *                             specialApr: true,
 *                             totalInterest: 3245,
 *                         },
 *                     ],
 *                 },
 *                 leaseOffers: {
 *                     36: [
 *                         {
 *                             acquisitionFee: 645,
 *                             annualMiles: 12000,
 *                             capCost: {
 *                                 adjustedCapCost: 35939.35,
 *                                 grossCapCost: 39355,
 *                                 totalCapCostReduction: 3415.65,
 *                             },
 *                             downPayment: 3366,
 *                             dueInAdvance: {
 *                                 dueAtSigning: 3871,
 *                                 firstPayment: 455.35,
 *                             },
 *                             excessMileageCharge: 0.2,
 *                             offeredBy: 'Ford Motor Credit',
 *                             payment: 455.35,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             programEndDate: '2021-11-01',
 *                             programStartDate: '2021-10-05',
 *                             specialOfferValue: 1000,
 *                             totalOfPayments: 16392.6,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                         },
 *                     ],
 *                     48: [
 *                         {
 *                             acquisitionFee: 678,
 *                             annualMiles: 12000,
 *                             capCost: {
 *                                 adjustedCapCost: 45939.35,
 *                                 grossCapCost: 49355,
 *                                 totalCapCostReduction: 4415.65,
 *                             },
 *                             downPayment: 6677,
 *                             dueInAdvance: {
 *                                 dueAtSigning: 3871,
 *                                 firstPayment: 567.89,
 *                             },
 *                             excessMileageCharge: 0.2,
 *                             offeredBy: 'Ford Motor Credit',
 *                             payment: 567.89,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             programEndDate: '2021-11-01',
 *                             programStartDate: '2021-10-05',
 *                             specialOfferValue: 1000,
 *                             totalOfPayments: 16392.6,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                         },
 *                     ],
 *                 },
 *                  cashOffers: {
 *                     -1: [
 *                         {
 *                             excessMileageCharge: 0.0,
 *                             payment: 0.0,
 *                             pricingDetails: {
 *                                 msrp: 72425,
 *                                 rebate: 2500,
 *                                 salePrice: 70224,
 *                                 specialOfferValue: -299
 *                             },
 *                             programEndDate: "2024-07-03",
 *                             programStartDate: "2024-07-03",
 *                             totalOfPayments: 0.0,
 *                             new: 0.0,
 *                             cashAmount: 35629,
 *                             taxesAndFees: 3630,
 *                             taxes: [
 *                                 {
 *                                     name: "advanced",
 *                                     amount: 3064,
 *                                     breakdown: {
 *                                         stateTax: 2015.78,
 *                                         countyTax: 322.53,
 *                                         cityTax: 403.16,
 *                                         localTax: 322.53,
 *                                         stateTaxRate: 6.25,
 *                                         countyTaxRate: 1,
 *                                         cityTaxRate: 1.25,
 *                                         localTaxRate: 1
 *                                     }
 *                                 },
 *                                 {
 *                                     name: "",
 *                                     amount: 0
 *                                 }
 *                             ],
 *                             fees: [
 *                                 {
 *                                     name: "Documentation Fee",
 *                                     amount: 250,
 *                                     capIndicator: true,
 *                                     category: "Dealer",
 *                                     feeDescription: "Documentation Fee",
 *                                     feeTypeId: 3,
 *                                     includedInCalc: true,
 *                                     taxAmount: 23.75,
 *                                     taxRate: 0.095
 *                                 },
 *                                 {
 *                                     name: "Registration Fee",
 *                                     amount: 151,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Registration Fee",
 *                                     feeTypeId: 2,
 *                                     includedInCalc: true
 *                                 },
 *                                 {
 *                                     name: "Title Fee",
 *                                     amount: 165,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Title Fee",
 *                                     feeTypeId: 5,
 *                                     includedInCalc: true
 *                                 }
 *                             ]
 *                         }
 *                     ]
 *                 },
 *             },
 *             b: {
 *                 paymentOffers: {
 *                     36: [
 *                         {
 *                             offeredBy: 'Mercedes-Benz Financial Services USA LLC.',
 *                             payment: 4444.35,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             new: 7.94,
 *                             downPayment: 4444,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                             totalOfPayments: 16392.6,
 *                             specialApr: true,
 *                             totalInterest: 3245,
 *                         },
 *                     ],
 *                     60: [
 *                         {
 *                             offeredBy: 'Mercedes-Benz Financial Services USA LLC.',
 *                             payment: 4444.35,
 *                             new: 7.94,
 *                             downPayment: 4444,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                             totalOfPayments: 16392.6,
 *                             specialApr: true,
 *                             totalInterest: 3245,
 *                         },
 *                     ],
 *                 },
 *                 leaseOffers: {
 *                     36: [
 *                         {
 *                             acquisitionFee: 645,
 *                             annualMiles: 12000,
 *                             capCost: {
 *                                 adjustedCapCost: 35939.35,
 *                                 grossCapCost: 39355,
 *                                 totalCapCostReduction: 3415.65,
 *                             },
 *                             downPayment: 3366,
 *                             dueInAdvance: {
 *                                 dueAtSigning: 3871,
 *                                 firstPayment: 455.35,
 *                             },
 *                             excessMileageCharge: 0.2,
 *                             offeredBy: 'Ford Motor Credit',
 *                             payment: 455.35,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             programEndDate: '2021-11-01',
 *                             programStartDate: '2021-10-05',
 *                             specialOfferValue: 1000,
 *                             totalOfPayments: 16392.6,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                         },
 *                     ],
 *                     48: [
 *                         {
 *                             acquisitionFee: 678,
 *                             annualMiles: 12000,
 *                             capCost: {
 *                                 adjustedCapCost: 45939.35,
 *                                 grossCapCost: 49355,
 *                                 totalCapCostReduction: 4415.65,
 *                             },
 *                             downPayment: 6677,
 *                             dueInAdvance: {
 *                                 dueAtSigning: 3871,
 *                                 firstPayment: 567.89,
 *                             },
 *                             excessMileageCharge: 0.2,
 *                             offeredBy: 'Ford Motor Credit',
 *                             payment: 567.89,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             programEndDate: '2021-11-01',
 *                             programStartDate: '2021-10-05',
 *                             specialOfferValue: 1000,
 *                             totalOfPayments: 16392.6,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                         },
 *                     ],
 *                 },
 *                  cashOffers: {
 *                     -1: [
 *                         {
 *                              excessMileageCharge: 0.0,
 *                             payment: 0.0,
 *                             pricingDetails: {
 *                                 msrp: 72425,
 *                                 rebate: 2500,
 *                                 salePrice: 70224,
 *                                 specialOfferValue: -299
 *                             },
 *                             programEndDate: "2024-07-03",
 *                             programStartDate: "2024-07-03",
 *                             totalOfPayments: 0.0,
 *                             new: 0.0,
 *                             cashAmount: 35629,
 *                             taxesAndFees: 3630,
 *                             taxes: [
 *                                 {
 *                                     name: "advanced",
 *                                     amount: 3064,
 *                                     breakdown: {
 *                                         stateTax: 2015.78,
 *                                         countyTax: 322.53,
 *                                         cityTax: 403.16,
 *                                         localTax: 322.53,
 *                                         stateTaxRate: 6.25,
 *                                         countyTaxRate: 1,
 *                                         cityTaxRate: 1.25,
 *                                         localTaxRate: 1
 *                                     }
 *                                 },
 *                                 {
 *                                     name: "",
 *                                     amount: 0
 *                                 }
 *                             ],
 *                             fees: [
 *                                 {
 *                                     name: "Documentation Fee",
 *                                     amount: 250,
 *                                     capIndicator: true,
 *                                     category: "Dealer",
 *                                     feeDescription: "Documentation Fee",
 *                                     feeTypeId: 3,
 *                                     includedInCalc: true,
 *                                     taxAmount: 23.75,
 *                                     taxRate: 0.095
 *                                 },
 *                                 {
 *                                     name: "Registration Fee",
 *                                     amount: 151,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Registration Fee",
 *                                     feeTypeId: 2,
 *                                     includedInCalc: true
 *                                 },
 *                                 {
 *                                     name: "Title Fee",
 *                                     amount: 165,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Title Fee",
 *                                     feeTypeId: 5,
 *                                     includedInCalc: true
 *                                 }
 *                             ]
 *                         }
 *                     ]
 *                 },
 *             },
 *         },
 *  }
 */
export const UDS_PAYMENT_UPDATE_COMPLETED = 'uds:paymentUpdate:completed';

/**
 * @name UDS_PAYMENT_CALC_RESPONSE_UPDATE
 * @description This event sent by the external tenant when updated payment info is provided as a result of new pencilling data sent as part of request 'UDS_PAYMENT_UPDATE_REQUEST'
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:PaymentCalcResponse:Update', payload)
 * @example <caption>Event Payload</caption>
 * {
 *     rate: {
 *             a: {
 *                 paymentOffers: {
 *                     36: [
 *                         {
 *                             offeredBy: 'Mercedes-Benz Financial Services USA LLC.',
 *                             payment: 3333.35,
 *                             incentiveIds: [
                                    182309,
                                    185916
                              ],
 *                             new: 7.94,
 *                             downPayment: 4444,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                             totalOfPayments: 16392.6,
 *                             specialApr: true,
 *                             totalInterest: 3245,
 *                         },
 *                     ],
 *                 },
 *                 leaseOffers: {
 *                     36: [
 *                         {
 *                             acquisitionFee: 645,
 *                             annualMiles: 12000,
 *                             capCost: {
 *                                 adjustedCapCost: 35939.35,
 *                                 grossCapCost: 39355,
 *                                 totalCapCostReduction: 3415.65,
 *                             },
 *                             downPayment: 3366,
 *                             dueInAdvance: {
 *                                 dueAtSigning: 3871,
 *                                 firstPayment: 455.35,
 *                             },
 *                             excessMileageCharge: 0.2,
 *                             offeredBy: 'Ford Motor Credit',
 *                             payment: 455.35,
 *                             pricingDetails: {
 *                                 rebate: 3250,
 *                                 specialOfferValue: 300,
 *                                 salePrice: 30000,
 *                                 msrp: 31000,
 *                             },
 *                             programEndDate: '2021-11-01',
 *                             programStartDate: '2021-10-05',
 *                             specialOfferValue: 1000,
 *                             totalOfPayments: 16392.6,
 *                             taxesFees: {
 *                                 dealerFees: 0.0,
 *                                 dmvFees: 43.0,
 *                                 lenderFees: 0.0,
 *                                 totalTax: 2442.3,
 *                                 totalTaxesFees: 2485.3,
 *                             },
 *                         },
 *                     ],
 *                 },
 *                  cashOffers: {
 *                     -1: [
 *                         {
 *                             excessMileageCharge: 0.0,
 *                             incentiveIds: [150031, 151941],
 *                             payment: 0.0,
 *                             pricingDetails: {
 *                                 msrp: 72425,
 *                                 rebate: 2500,
 *                                 salePrice: 70224,
 *                                 specialOfferValue: -299
 *                             },
 *                             programEndDate: "2024-07-03",
 *                             programStartDate: "2024-07-03",
 *                             totalOfPayments: 0.0,
 *                             new: 0.0,
 *                             cashAmount: 35629,
 *                             taxesAndFees: 3630,
 *                             taxes: [
 *                                 {
 *                                     name: "advanced",
 *                                     amount: 3064,
 *                                     breakdown: {
 *                                         stateTax: 2015.78,
 *                                         countyTax: 322.53,
 *                                         cityTax: 403.16,
 *                                         localTax: 322.53,
 *                                         stateTaxRate: 6.25,
 *                                         countyTaxRate: 1,
 *                                         cityTaxRate: 1.25,
 *                                         localTaxRate: 1
 *                                     }
 *                                 },
 *                                 {
 *                                     name: "",
 *                                     amount: 0
 *                                 }
 *                             ],
 *                             fees: [
 *                                 {
 *                                     name: "Documentation Fee",
 *                                     amount: 250,
 *                                     capIndicator: true,
 *                                     category: "Dealer",
 *                                     feeDescription: "Documentation Fee",
 *                                     feeTypeId: 3,
 *                                     includedInCalc: true,
 *                                     taxAmount: 23.75,
 *                                     taxRate: 0.095
 *                                 },
 *                                 {
 *                                     name: "Registration Fee",
 *                                     amount: 151,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Registration Fee",
 *                                     feeTypeId: 2,
 *                                     includedInCalc: true
 *                                 },
 *                                 {
 *                                     name: "Title Fee",
 *                                     amount: 165,
 *                                     capIndicator: true,
 *                                     category: "Government",
 *                                     feeDescription: "Title Fee",
 *                                     feeTypeId: 5,
 *                                     includedInCalc: true
 *                                 }
 *                             ]
 *                         }
 *                     ]
 *                 },
 *             },
 *    },
 *    dealType: finance,
 *    creditType: very good,
 *    loanTerm: 60,
 *    leaseTerm: 60,
 *    tradeInValue: 2000,
 *    amountOwed: 500,
 *    customRate: null,
 *    paymentErrorState: false,
 *    supportedDealTypes: ['finance', cash'],
 *    isFinanceErrorState: false,
 *    isLeaseErrorState: false,
 *    includeTaxesAndFees: false
 *  }
 */
export const UDS_PAYMENT_CALC_RESPONSE_UPDATE = 'uds:PaymentCalcResponse:Update';

/**
 * @name UDS_PAYMENT_OBJ_UPDATE
 * @description Event sent by the UDS Calculator to update payments values when paymentCalc is updated. Once a subset of data is passed into the payload, it will be used for UDS Tabs.
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:PaymentObject:Update', payload)
 * @example <caption>Event Payload</caption>
 * [
    {
        downPayment: 6300,
        excessMileageCharge: 0,
        offeredBy: "Ally",
        payment: 492.76,
        pricingDetails: {
            salePrice: 19990,
            salePriceBasedOnMSRP: false,
            originalSalePrice: 19990
        },
        programEndDate: "",
        programStartDate: "2025-04-01",
        taxesFees: {
            dealerFees: 0,
            dmvFees: 43,
            lenderFees: 0,
            totalTax: 1399.3,
            totalTaxesFees: 1442.3
        },
        totalOfPayments: 17739.36,
        totalInterest: 2607.06,
        specialApr: false,
        used: 10.63,
        paymentLabel: "finance",
        loanTerm: 36,
        taxesAndFees: 1442.3,
        isPaymentError: false
    },
    {
        paymentLabel: "lease",
        loanTerm: 36,
        taxesAndFees: 0,
        isPaymentError: true
    },
    {
        excessMileageCharge: 0,
        payment: 21432.3,
        pricingDetails: {
            salePrice: 19990,
            salePriceBasedOnMSRP: false,
            originalSalePrice: 19990
        },
        programEndDate: "2025-06-03",
        programStartDate: "2025-06-03",
        taxesFees: {
            dealerFees: 0,
            dmvFees: 43,
            lenderFees: 0,
            totalTax: 1399.3,
            totalTaxesFees: 1442.3
        },
        totalOfPayments: 0,
        totalInterest: 0,
        specialApr: false,
        used: 0,
        paymentLabel: "cash",
        taxesAndFees: 1442.3
    }
]
 */
export const UDS_PAYMENT_OBJ_UPDATE = 'uds:PaymentObject:Update';

/**
 * @name UDS_PENCILING_DATA_UPDATED
 * @description Event sent by the UDS calculator to update payment values when modifications (AKA penciling) occurs.  This is a basic set of the display values instead of the more complete payment information.
 * @example <caption>Event Code</caption>
 * eventBus.emit(uds:calcPencilingDataUpdate:updated, {
 *   creditTier: "a",
 *   dealType: "finance",
 *   downPayment: 17004,
 *   offerPrice: 12345,
 *   term: 48
 * });
 * @example <caption>Event Payload</caption>
 * {
 *   creditTier: "a",
 *   dealType: "finance",
 *   downPayment: 17004,
 *   offerPrice: 12345,
 *   term: 48
 * }
 */
export const UDS_PENCILING_DATA_UPDATED = 'uds:calcPencilingDataUpdate:updated';

/**
 * @name UDS_OPEN_SDP_REQUEST
 * @description Event sent to initialize the SDP widget via wallet-manager
 * @example <caption>Event Code</caption>
 * eventBus.emit(uds:openSdp:request, {
 *   hooks: {}, // optional
 *   isMobile: 'no', // optional
 *   pixall: {}, // optional
 *   route: 'shopperLeadForm', // required. For a complete list of valid routes, see table here: https://coxautoinc.sharepoint.com/sites/DigitalRetail3_0Platform/SitePages/Cox-Auto-Digital-Retailing---Shopper-Detail-Page-Widget-Integration.aspx#initialization-object
 *   shopper: {}, // optional
 *   listing: {}, // optional
 *   listedPrice: "" // optional
 *   paymentsInfo: null // optional
 * });
 * @example <caption>Event Payload</caption>
 * {
 *   hooks: {
 *     onHidingFunction: () => {},
 *     onLoadedFunction: () => {},
 *     onTrack: () => {},
 * },
 *   isMobile: 'no',
 *   pixall: {
 *     initiator: 'shopper',
 *     referringSource: 'My Wallet NDS'
 *   },
 *   route: 'shopperLeadForm',
 *   shopper: {
 *     firstName: 'John',
 *     lastName: 'Wick',
 *     email: jwick@gmail.com,
 *     zip: 30066,
 *   }
 *   listing: {},
 *   listedPrice: 60000,
 *   paymentsInfo: {
 *     creditType: "verygood",
 *     dealType: "finance",
 *     loanTerm: 60,
 *     leaseTerm: 36,
 *     downPayment: 7000,
 *   },
 * }
 */
export const UDS_OPEN_SDP_REQUEST = 'uds:openSdp:request';

/**
 * Sends name, action, and data from the SDP widget onTrack event
 */
/**
 * @name UDS_SDP_ONTRACK_COMPLETED
 * @description Event emitted by the SDP widget onTrack event
 * @example <caption>Event Code</caption>
 * eventBus.emit(uds:sdpOnTrack:completed, {
 *   name: "DR_ZIP_CODE_UPDATED",
 *   action: "DR Action",
 *   data: {
 *       dealType: "finance",
 *       dr: {
 *           pageType: "vdp",
 *           eventOwner: "CAI",
 *           vehicle: {
 *               vin: "4T1B11HK2KU854495",
 *               stockNumber: "",
 *               condition: "Used",
 *               year: "2019",
 *               make: "Toyota",
 *               model: "",
 *               trim: "",
 *               color: "",
 *               type: "",
 *               fuel: "",
 *               inventoryDate: "",
 *               price: 200500
 *           }
 *       }
 *   }
 * });
 * @example <caption>Event Payload</caption>
 {
 *   name: "DR_ZIP_CODE_UPDATED",
 *   action: "DR Action",
 *   data: {
 *       dealType: "finance",
 *       dr: {
 *           pageType: "vdp",
 *           eventOwner: "CAI",
 *           vehicle: {
 *               vin: "4T1B11HK2KU854495",
 *               stockNumber: "",
 *               condition: "Used",
 *               year: "2019",
 *               make: "Toyota",
 *               model: "",
 *               trim: "",
 *               color: "",
 *               type: "",
 *               fuel: "",
 *               inventoryDate: "",
 *               price: 200500
 *           }
 *       }
 *   }
 * }
 */
export const UDS_SDP_ONTRACK_COMPLETED = 'uds:sdpOnTrack:completed';

/**
 * @name UDS_SET_DEALTYPE_REQUEST
 * @description Event sent by the external tenant to set the deal type (`finance`, `lease`, `cash`)
 * @example <caption>Event Code</caption>
 * window.caiEventBus.emit('uds:setDealType:request', {
 *     dealType: 'finance',
 * });
 * @example <caption>Event Payload</caption>
 * {
 *     dealType: 'finance',
 * }
 */
export const UDS_SET_DEALTYPE_REQUEST = 'uds:setDealType:request';

/**
 * @name UDS_SET_DEALTYPE_COMPLETED
 * @description Event sent by the UDS calculator to indicate that the deal type has been set successfully
 */
export const UDS_SET_DEALTYPE_COMPLETED = 'uds:setDealType:completed';

/**
 * @description requests to retrieve available incentives
 * @example
 * window.caiEventBus.emit('wallet:getIncentives:request');
 */
export const UDS_GET_INCENTIVES_REQUEST = 'uds:getIncentives:request';

/**
 * @description returns available incentives
 * @example
 * window.caiEventBus.emit('wallet:getIncentives:completed', [
 *   {
 *     cash: 500,
 *     conditionCategory: "Military",
 *     description: "...",
 *     endDate: "2025-01-02T00:00:00",
 *     financeMethod: "All",
 *     fundingSource: "Ford Motor Company",
 *     incentiveId: 135506,
 *     incentiveType: "Customer Cash",
 *     programName: "2024 Ford Military Recognition Exclusive Cash Reward",
 *     programType: "Conditional",
 *     standardRebateIndicator: "false",
 *     startDate: "2024-01-03T00:00:00",
 *     termRebates: {
 *       incentiveTermRange: [{
 *         apr: 1.9,
 *         customerCash: 0,
 *         endTerm: 36,
 *         moneyFactor: 0,
 *         startTerm: 12
 *       }]
 *     }
 *   }
 * ]);
 */
export const UDS_GET_INCENTIVES_COMPLETED = 'uds:getIncentives:completed';

/**
 * @description Request sent to external tenant for processing the CTA click event
 * @example
 * window.caiEventBus.emit('uds:ctaClick:request', {
 *    origin: 'continue-button', (also supports 'credit-verification-cta')
 *    term: 36,
 *    creditScore: 'verygood',
 *    downPayment: 12000,
 *    offerPrice: 32999,
 *    monthlyPayment: 369,
 *    rate: 7.5,
 *    lender: "Ford Motor Credit",
 * });
 */

export const UDS_CTA_CLICK_REQUEST = 'uds:ctaClick:request';

/**
 * @description Response from the external tenant after processing the `UDS_CTA_CLICK_REQUEST` event
 * @example
 * window.caiEventBus.emit('uds:ctaClick:completed', {
 *    creditScore: 'verygood',
 *    verificationError: false,
 * });
 */

export const UDS_CTA_CLICK_COMPLETED = 'uds:ctaClick:completed';

/**
 * @name UDS_OPEN_BOA_CREDIT_APP_REQUEST
 * @description Trigger event to initiate a Bank of America credit application. When this event is emitted (typically from a button click), the application will gather the necessary vehicle, dealer, and payment data internally, validate it, and submit a credit application form in a new browser tab.
 * @example <caption>Event Code</caption>

 * window.caiEventBus.emit('UDS_OPEN_BOA_CREDIT_APP_REQUEST', {
 *    // Optional configuration parameters can be added here if needed
 * });
 * @example <caption>Event Payload</caption>
 * {
 *    // This is a trigger event - no specific payload required
 *    // The application will gather required data from current context:
 *    // - Dealer information from current config
 *    // - Vehicle information from inventory data
 *    // - Payment information from state
 * }
 * @example <caption>Internal Data Structure (for BoA Credit Application)</caption>
 * // The hook internally expects this data structure to be available:
 * {
 *   "dealerInfo": {
 *     "dealerId": "2090",
 *     "name": "Akins Ford Chrysler Jeep",
 *     "addressLine1": "220 W May St",
 *     "city": "Winder",
 *     "state": "GA",
 *     "zipCode": "30680",
 *     "phoneNumber": "770-867-9136"
 *   },
 *   "vehicleInfo": {
 *     "vehicleCondition": "NEW",
 *     "year": "2025",
 *     "make": "Ford",
 *     "model": "Expedition",
 *     "trim": "Active 4x2",
 *     "interiorColor": "Space Gray",
 *     "exteriorColor": "Dark Matter Gray Metallic",
 *     "chromeStyleID": "467462",
 *     "stockNumber": "SEA06518",
 *     "certifiedUsed": "N",
 *     "mileage": "0",
 *     "photoUrl": "https://api.dtdrs.dealertrack.com/imgproxy/...",
 *     "vin": "1FMJU1H89SEA06518"
 *   },
 *   "paymentInfo": {
 *     "msrp": "68833",
 *     "price": "68833",
 *     "downPayment": "0",
 *     "rebate": "0",
 *     "term": "60",
 *     "netTrade": "3000",
 *     "dueAtSign": "0",
 *     "creditTier": "Tier 1",
 *     "estimatedPayment": "1313",
 *     "loanAmount": "68833",
 *     "apr": "7.94",
 *     "selfReportedCredit": "Very Good"
 *   }
 * }
 */

export const UDS_OPEN_BOA_CREDIT_APP_REQUEST = 'uds:openBOACreditApp:request';

/**
 * @name UDS_OPEN_BOA_CREDIT_APP_COMPLETED
 * @description Response event indicating the result of the Bank of America credit application request processing. Emitted after the application attempts to gather data, validate it, and submit the form. Contains success status and any error details from validation or submission.
 * @example <caption>Success Response</caption>
 * window.caiEventBus.emit('UDS_OPEN_BOA_CREDIT_APP_COMPLETED', {
 *    success: true,
 *    message: "Credit application opened successfully"
 * });
 * @example <caption>Validation Error Response</caption>
 * window.caiEventBus.emit('UDS_OPEN_BOA_CREDIT_APP_COMPLETED', {
 *    success: false,
 *    error: "Validation failed: Vehicle year must be a valid year between 1900 and 2027, VIN must be exactly 17 characters and contain only valid alphanumeric characters, Vehicle make is required",
 *    errors: [
 *        // Dealer Info Validations (7 fields)
 *        "Dealer ID is required",
 *        "Dealer name is required",
 *        "Dealer address line 1 is required",
 *        "Dealer city is required",
 *        "Dealer state is required",
 *        "Dealer zip code is required",
 *        "Dealer phone number is required",
 *
 *        // Vehicle Info Validations (13 fields)
 *        "Vehicle year must be a valid year between 1900 and 2027",
 *        "VIN must be exactly 17 characters and contain only valid alphanumeric characters",
 *        "Photo URL must be a valid image URL (jpg, jpeg, png, gif, or webp)",
 *        "Vehicle make is required",
 *        "Vehicle model is required",
 *        "Vehicle condition is required",
 *        "Vehicle trim is required",
 *        "Vehicle interior color is required",
 *        "Vehicle exterior color is required",
 *        "Vehicle stock number is required",
 *        "Vehicle mileage must be a valid non-negative number",
 *        "MSRP must be a valid non-negative number",
 *        "Down payment must be a valid non-negative number",
 *
 *        // Payment Info Validations (12 fields)
 *        "Credit tier is required",
 *        "Vehicle price must be a valid positive number",
 *        "Loan amount must be a valid non-negative number",
 *        "APR must be a valid non-negative number",
 *        "Loan term must be a valid positive number",
 *        "Self-reported credit is required",
 *        "Rebate must be a valid non-negative number",
 *        "Net trade must be a valid number",
 *        "Due at signing must be a valid non-negative number",
 *        "Estimated payment must be a valid positive number"
 *    ]
 * });
 * @example <caption>Data Gathering Error Response</caption>
 * window.caiEventBus.emit('UDS_OPEN_BOA_CREDIT_APP_COMPLETED', {
 *    success: false,
 *    error: "Data unavailable",
 * });
 * @example <caption>Event Payload</caption>
 * {
 *    success: boolean,         // Required - Whether the operation was successful
 *    message?: string,         // Optional - Success message when successful
 *    error?: string,           // Optional - Error message when unsuccessful (includes concatenated validation errors)
 *    errors?: string[]         // Optional - Array of individual validation error messages (available when validation fails)
 *    validation?: {            // Optional - Validation details when successful
 *        isValid: boolean,
 *        validatedData: object
 *    },
 *    formData?: object,        // Optional - Processed form data when successful
 *    formFields?: array        // Optional - Form field structure when successful
 * }
 */

export const UDS_OPEN_BOA_CREDIT_APP_COMPLETED = 'uds:openBOACreditApp:completed';
